
import { useEffect } from 'react';
import { FastingProtocol } from '@/types/fasting';
import { useFastingHistory } from './FastingHistoryContext';
import { 
  useTimerState,
  useTimerInitialization,
  useTimerStateSync,
  useTimerInterval,
  useVisibilityChange
} from './timer';

interface TimerLogicProps {
  initialIsActive?: boolean;
  initialIsPaused?: boolean;
  initialTimeElapsed?: number;
  initialStartTime?: number | null;
  initialPausedTime?: number | null;
  initialTotalPauseDuration?: number;
  selectedProtocol: FastingProtocol;
  isLoaded?: boolean; // Add flag to wait for persistence loading
}

export const useTimerLogic = ({
  initialIsActive = false,
  initialIsPaused = false,
  initialTimeElapsed = 0,
  initialStartTime = null,
  initialPausedTime = null,
  initialTotalPauseDuration = 0,
  selectedProtocol,
  isLoaded = true // Default to true for backward compatibility
}: TimerLogicProps) => {
  const fastingHistory = useFastingHistory();

  // Initialize timer state
  const timerState = useTimerState({
    initialIsActive,
    initialIsPaused,
    initialTimeElapsed,
    initialStartTime,
    initialPausedTime,
    initialTotalPauseDuration
  });

  const {
    isActive,
    setIsActive,
    isPaused,
    setIsPaused,
    timeElapsed,
    setTimeElapsed,
    startTime,
    setStartTime,
    pausedTime,
    setPausedTime,
    totalPauseDuration,
    setTotalPauseDuration,
    intervalRef,
    initRef
  } = timerState;

  // Calculate calories burned dynamically based on current timeElapsed
  const estimatedCaloriesBurned = Math.round(timeElapsed / 3600 * 55);

  // Debug any state changes
  useEffect(() => {
    console.log("[useTimerLogic] State updated:", {
      isActive,
      isPaused,
      timeElapsed,
      startTime: startTime ? new Date(startTime).toLocaleString() : 'none'
    });
  }, [isActive, isPaused, timeElapsed, startTime]);
  // Initialize timer with saved state
  useTimerInitialization({
    initialIsActive,
    initialStartTime,
    initialTimeElapsed,
    initialTotalPauseDuration,
    initRef,
    setTimeElapsed,
    setIsActive,
    setStartTime,
    selectedProtocol,
    isLoaded
  });

  // Synchronize timer state
  useTimerStateSync({
    isActive,
    startTime,
    setStartTime
  });

  // Handle timer intervals
  useTimerInterval({
    isActive,
    isPaused,
    startTime,
    pausedTime,
    totalPauseDuration,
    timeElapsed,
    selectedProtocol,
    intervalRef,
    setTimeElapsed,
    setPausedTime,
    setTotalPauseDuration,
    setIsActive
  });

  // Handle visibility changes
  useVisibilityChange({
    isActive,
    isPaused,
    startTime,
    totalPauseDuration,
    selectedProtocol,
    setTimeElapsed
  });

  return {
    isActive,
    setIsActive,
    isPaused,
    setIsPaused,
    timeElapsed,
    setTimeElapsed,
    startTime,
    setStartTime,
    pausedTime,
    setPausedTime,
    totalPauseDuration,
    setTotalPauseDuration,
    estimatedCaloriesBurned,
    fastingHistory
  };
};
